import { Controller } from "react-hook-form";
import { MultiSelect, Stack, Group } from "@mantine/core";
import { Checkbox } from "@onengine/component-library";
import { FormErrorBox } from "@/components/ui/FormErrorBox/FormErrorBox";
import { OtherTextInput } from "./OtherTextInput";
import type { ControlledFieldProps } from "./types";

const OTHER_VALUE = "__other__";

export function ControlledMultiSelect({
  name,
  control,
  error,
  clearErrors,
  data,
  placeholder,
  allowOther,
}: ControlledFieldProps & {
  data: { value: string; label: string }[];
  placeholder?: string;
  allowOther?: boolean;
}) {
  const filteredData = allowOther ? data.filter((o) => o.label !== "Other") : data;

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field }) => {
        const selected: string[] = Array.isArray(field.value) ? field.value : [];
        const isOtherChecked = selected.includes(OTHER_VALUE);

        return (
          <Stack gap="xs">
            <MultiSelect
              value={selected.filter((v) => v !== OTHER_VALUE)}
              onChange={(v) => {
                field.onChange(isOtherChecked ? [...v, OTHER_VALUE] : v);
                clearErrors(name);
              }}
              data={filteredData}
              placeholder={placeholder || "Select options"}
              size="sm"
              radius="md"
              error={!!error}
            />
            {error && <FormErrorBox message={error} />}
            {allowOther && (
              <Group gap="sm" align="flex-start" wrap="nowrap">
                <Checkbox
                  label="Other"
                  size="small"
                  state={isOtherChecked ? "selected" : "deselected"}
                  onValueChange={(checked) => {
                    const next = checked
                      ? [...selected, OTHER_VALUE]
                      : selected.filter((v) => v !== OTHER_VALUE);
                    field.onChange(next);
                    clearErrors(name);
                  }}
                />
                <OtherTextInput
                  name={`${name}__other`}
                  control={control}
                  disabled={!isOtherChecked}
                />
              </Group>
            )}
          </Stack>
        );
      }}
    />
  );
}
